import { Injectable } from '@angular/core';
import { FileUpload } from './file-upload';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { API, AssetFileDTO, AssetFileFullDTO, AttachmentDTO } from '../api/api';
import { ToasterService, ToastType } from '../toaster/toaster.service';
import { ErrorService } from '../error.service';
import { ModalService } from '../gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../util.service';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private _isUploading: boolean;
  private _currentlyUploading: BehaviorSubject<boolean>;
  private _files: FileUpload[] = [];
  private _uploadQueue: BehaviorSubject<FileUpload[]>;
  public _finishedUpload: BehaviorSubject<any>;
  private $destroyUpload: Subject<boolean>;
  public $allUploadFinished: Subject<boolean>;

  constructor(
    public api: API,
    public toastHandler: ToasterService,
    public errorHandler: ErrorService,
    public modalService: ModalService,
    public translate: TranslateService,
    public util: UtilService
  ) {
    this.$allUploadFinished = new Subject<boolean>();
    this._finishedUpload = new BehaviorSubject(null);
    this._isUploading = false;
    this.$destroyUpload = new Subject();
    this._currentlyUploading = new BehaviorSubject(this._isUploading);
    this._uploadQueue = new BehaviorSubject([]);
  }

  removeFile(file: FileUpload) {
    switch (true) {
      case file.status === 'Started':
        this.destroyUpload();
        break;
      case file.status === 'NotStarted':
        for (const [i, f] of this._files.entries()) {
          if (f.file?.name === file.file?.name) {
            this._files.splice(i, 1);
            return;
          }
        }
        break;
      case file.status === 'Error':
        for (const [i, f] of this._files.entries()) {
          if (f.file?.name === file.file?.name) {
            this._files.splice(i, 1);
            return;
          }
        }
        break;
      default:
      // check if default is necessary
    }
  }

  destroyUpload() {
    this.$destroyUpload.next(null);
    this.$destroyUpload.complete();
  }

  dropHandler(event, pType: string, parent?: any) {
    const uploadFiles = [];
    if (event.dataTransfer.items) {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        // necessary for file transfer
        if (event.dataTransfer.items[i].kind === 'file') {
          const file = event.dataTransfer.items[i].getAsFile();
          uploadFiles.push(file);
        }
      }
    } else {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < event.dataTransfer.files.length; i++) {
        // necessary for file transfer
        const f = event.dataTransfer.files[i];
        uploadFiles.push(f);
      }
    }

    this.handleFileUpload(uploadFiles, pType, parent);
  }

  handleFileUpload(files: File[], pType: string, parent?: any) {
    for (const file of files) {
      this.addToQueue(file, pType, parent);
      // this.uploadAttachment(file); rem
    }
  }

  public get queue() {
    return this._uploadQueue;
  }

  public addToQueue(pFile: File, pType: string, parent?: any) {
    const queuedUploadFile = new FileUpload(pFile, pType, parent);
    this._files.push(queuedUploadFile);

    this._uploadQueue.next(this._files);
    this.checkAndUploadNextFile();
  }

  private checkAndUploadNextFile() {
    if (this._isUploading) {
      return;
    }

    const firstChoice = this._files.find(f => f.isWaitingForUpload);
    if (firstChoice) {
      this.upload(firstChoice);
    } else {
      let unfinished: boolean = false;
      for (const file of this._files) {
        if (file.status === 'NotStarted' || file.status === 'Started') {
          unfinished = true;
        }
      }
      if (!unfinished) {
        this.$allUploadFinished.next(true);
      }
    }
  }

  private upload(queuedUploadFile: FileUpload) {
    this.isUploading = true;
    queuedUploadFile.status = 'Started';
    // queuedUploadFile.updateProgress(0); // progress ?
    if (queuedUploadFile.type === 'attachment') {
      this.api
        .shared_Attachment_uploadAttachment(queuedUploadFile.data)
        .pipe(
          takeUntil(this.$destroyUpload),
          finalize(() => {
            // Upload finished, no matter whether complete or error
            this.isUploading = false;
            this.checkAndUploadNextFile();
          })
        )
        .subscribe({
          next: (att: AttachmentDTO) => {
            queuedUploadFile.data = att;
            // upload response?
            /*if (event.type === HttpEventType.UploadProgress) {
              const percentDone = Math.round(100 * event.loaded / event.total);
              queuedUploadFile.updateProgress(percentDone);
            } else if (event instanceof HttpResponse) {
              queuedUploadFile.completed();
            }
             */
            queuedUploadFile.uploaded();

            this._uploadQueue.next(this._files);
          },
          error: () => {
            queuedUploadFile.failed();
            this._uploadQueue.next(this._files);
          },
          complete: () => {
            if (queuedUploadFile.status === 'Started' || queuedUploadFile.status === 'NotStarted') {
              queuedUploadFile.failed();
            }
            if (queuedUploadFile.status === 'Uploaded') {
              this.processAttachment(queuedUploadFile).then((attachment: AttachmentDTO) => {
                queuedUploadFile.completed();
                this._finishedUpload.next(null);
                setTimeout(() => {
                  for (const [i, file] of this._files.entries()) {
                    if (file.data.id === attachment.id && file.status === 'Done') {
                      this._files.splice(i, 1);
                      this._finishedUpload.next(attachment);
                    }
                  }
                }, 5000);
              });
            }
          },
        });
    }
    if (queuedUploadFile.type === 'asset') {
      if (queuedUploadFile.parent.id) {
        this.api
          .dm_assets_Asset_uploadAssetFile(queuedUploadFile.parent.id, queuedUploadFile.data) // assetCall
          .pipe(
            takeUntil(this.$destroyUpload),
            finalize(() => {
              // Upload finished, no matter whether complete or error
              this.isUploading = false;
              this.checkAndUploadNextFile();
            })
          )
          .subscribe({
            next: (asset: AssetFileFullDTO) => {
              queuedUploadFile.data = asset;
              // // upload response?
              // if (event.type === HttpEventType.UploadProgress) {
              //  const percentDone = Math.round(100 * event.loaded / event.total);
              //  queuedUploadFile.updateProgress(percentDone);
              // } else if (event instanceof HttpResponse) {
              //  queuedUploadFile.completed();
              // }

              queuedUploadFile.uploaded();

              this._uploadQueue.next(this._files);
            },
            error: () => {
              queuedUploadFile.failed();
              this._uploadQueue.next(this._files);
            },
            complete: () => {
              if (queuedUploadFile.status <= 'Started') {
                queuedUploadFile.failed();
              }
              if (queuedUploadFile.status === 'Uploaded') {
                this.processAssetFile(queuedUploadFile).then((asset: AssetFileDTO) => {
                  queuedUploadFile.completed();
                  this._finishedUpload.next(null);
                  setTimeout(() => {
                    for (const [i, file] of this._files.entries()) {
                      if (file.data.id === asset.id && file.status === 'Done') {
                        this._files.splice(i, 1);
                        this._finishedUpload.next(asset);
                      }
                    }
                  }, 5000);
                });
              }
            },
          });
      }
    }
    if (queuedUploadFile.type === 'medium') {
      /*if (!this.medium) {
      this.toastHandler.toast('error', 'No medium provided');
      return;
    }
    this.api.mm_Medium_create(this.medium).subscribe((result: MediumFullDTO) => {
      this.medium.id = result.id;
      this.uploadMediumFile(this.medium, queuedUploadFile.data).then((upload: MediumUploadDTO) => {
        if (upload) {
          medium.processing = true;
          medium.uploading = undefined;
          this.checkForFileReady(medium);
          resolve(upload);
        } else {
          medium.uploading = undefined;
          this.api.mm_Medium_deleteMedium(medium.id).subscribe(() => {
            this.requestDebounced();
            this.resetFileUpload();
          });
          // reject('Canceled upload of existing content');
        }
      }).catch((error) => {
        medium.uploading = undefined;
        reject(error);
      });
    }, error => {
      reject(error);
    }, () => {
      this.medias = this.orderArrayOnElement(medium, this.medias);
    });
    /*
    this.api.shared_Attachment_uploadAttachment(queuedUploadFile.data)
      .pipe(
        takeUntil(this.$destroyUpload),
        finalize(() => {
          // Upload finished, no matter whether complete or error
          this.isUploading = false;
          this.checkAndUploadNextFile();
        })
      )
      .subscribe((att: AttachmentDTO) => {
        queuedUploadFile.data = att;
        // // upload response?
        // if (event.type === HttpEventType.UploadProgress) {
        //  const percentDone = Math.round(100 * event.loaded / event.total);
        //  queuedUploadFile.updateProgress(percentDone);
        //} else if (event instanceof HttpResponse) {
        //  queuedUploadFile.completed();
        //}
        queuedUploadFile.uploaded();
        this._uploadQueue.next(this._files);
      }, () => {
        queuedUploadFile.failed();
        this._uploadQueue.next(this._files);
      }, () => {
        if (queuedUploadFile.status <= 'Started') {
          queuedUploadFile.failed();
        }
        if (queuedUploadFile.status === 'Uploaded') {
          this.processAttachment(queuedUploadFile).then((attachment: AttachmentDTO) => {
            queuedUploadFile.completed();
            this._finishedUpload.next(null);
            setTimeout(() => {
              for (const [i, file] of this._files.entries()) {
                if (file.data.id === attachment.id && file.status === 'Done') {
                  this._files.splice(i, 1);
                  this._finishedUpload.next(attachment);
                }
              }
              this.checkAllUploadFinished();
            }, 5000);
          });
        }
      });
    */
    }
    if (!queuedUploadFile.type) {
      this.toastHandler.toast(ToastType.ALERT, 'No matching Type');
    }
  }

  private set isUploading(value: boolean) {
    this._isUploading = value;
    this._currentlyUploading.next(value);
  }

  private processAttachment(queuedUploadFile: FileUpload) {
    return new Promise<AttachmentDTO>(resolve => {
      this.api.shared_Attachment_retrieveById(queuedUploadFile.data.id).subscribe((attachment: AttachmentDTO) => {
        resolve(attachment);
      });
    });
  }

  private processAssetFile(queuedUploadFile: FileUpload) {
    return new Promise<AssetFileDTO>(resolve => {
      this.api
        .dm_assets_Asset_assetFileRetrieveById(queuedUploadFile.parent?.id, queuedUploadFile.data?.id)
        .subscribe((assetFile: AssetFileDTO) => {
          resolve(assetFile);
        });
    });
  }

  /*uploadMediumFile(medium: MediumDTO, file: File): Promise<MediumUploadDTO> {
                    return new Promise((resolve, reject) => {
                      if (medium?.id) {
                        this.md5Check(file).then((choice: boolean) => {
                          if (choice) {
                            const formData: FormData = new FormData();
                            formData.set('file', file);
                            this.previewMedium = medium as MediumUploadDTOAndTags;
                            this.api.mm_Medium_uploadFile(medium.id, formData).subscribe((result: MediumUploadDTO) => {
                              resolve(result);
                            }, error => {
                              reject(error);
                            });
                          } else {
                            resolve(null);
                          }
                        }).catch(error => {
                          reject(error);
                        });
                      } else {
                        reject('No Medium with ID was provided');
                      }
                    });
                  }

                  md5Check(file: File): Promise<boolean> {
                    return new Promise((resolve, reject) => {
                      this.util.computeChecksumMd5(file).then(r => {
                        this.api.mm_Medium_retrieveAll({hashMD5: r}).subscribe((data) => {
                          if (data.length > 0) {
                            this.modalService.confirm({
                              title: this.translate.instant('lang.globals.existingMotive'),
                              message: this.translate.instant('lang.globals.existingMotiveMessage'),
                              yes: this.translate.instant('lang.globals.yes'),
                              no: this.translate.instant('lang.globals.no')
                            }).then((result: boolean) => {
                              if (result) {
                                resolve(true);
                              } else {
                                resolve(false);
                              }
                            });
                          } else {
                            resolve(true);
                          }
                        }, error => {
                          reject(error);
                        });
                      }).catch(error => {
                        reject(error);
                      });
                    });
                  }

                   */
}
